import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Power, ListOrdered, Star, ExternalLink, ShieldCheck, ShieldX, Filter, Unlink, UserPlus, Users, Loader2, X, MapPin, Search, Building2, Phone, MessageCircle, FileText } from 'lucide-react';
import { adminApi, adminOwnersApi, adminLocationApprovalApi } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const empty = {
  id: '', name: '', category: 'restaurants', cuisine: '', rating: 4.5, reviews: 0,
  deliveryTime: '20–30 min', deliveryFee: 2.99, minOrder: 0, distance: '1.0 km',
  cover: '', logo: '', tags: '', promo: '', is_active: true,
};

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('filter');
    return ['pending', 'approved', 'rejected', 'pending_location', 'all'].includes(p) ? p : 'all';
  });  // all | pending | approved | rejected | pending_location
  const [rejectDialog, setRejectDialog] = useState({ open: false, vendor: null, reason: '' });
  const [reassignDialog, setReassignDialog] = useState({ open: false, vendor: null, email: '', busy: false });
  const [ownersDialog, setOwnersDialog] = useState({ open: false, vendor: null });
  const [locRejectDialog, setLocRejectDialog] = useState({ open: false, vendor: null, reason: '' });
  const [ownerLocDialog, setOwnerLocDialog] = useState({ open: false, vendor: null, form: null, saving: false });
  const [search, setSearch] = useState('');

  const load = async () => { try { setVendors(await adminApi.vendors()); } catch { /* */ } };
  useEffect(() => { load(); }, []);

  const setApproval = async (vendor, status, reason = null) => {
    try {
      const { data: doc } = await api.patch(`/admin/vendors/${vendor.id}/approval`, { status, reason });
      toast.success(status === 'approved' ? `${vendor.name} approved — sales enabled.`
                  : status === 'rejected' ? `${vendor.name} rejected — vendor notified.`
                  : `${vendor.name} reset to pending.`);
      if (doc?._admin_wa_link) {
        toast(status === 'approved' ? `Congratulate ${vendor.name} on WhatsApp?` : `Explain the rejection to ${vendor.name} on WhatsApp?`, {
          duration: 8000,
          action: {
            label: 'Open WhatsApp',
            onClick: () => window.open(doc._admin_wa_link, '_blank', 'noopener'),
          },
        });
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const q = search.trim().toLowerCase();
  const matchesSearch = (v) => {
    if (!q) return true;
    const bag = [
      v.id, v.name, v.category, v.cuisine, v.city, v.state, v.country,
      v.address, v.owner_name, v.owner_email, v.owner_phone, v.whatsapp_number,
      v.business_registration_id, v.tax_id, v.vendor_number,
      ...(v.owner_emails || []),
    ].filter(Boolean).join(' ').toLowerCase();
    return bag.includes(q);
  };
  const filtered = (filter === 'all'
    ? vendors
    : filter === 'pending_location'
      ? vendors.filter((v) => v.location_approval_status === 'pending')
      : vendors.filter((v) => (v.approval_status || 'approved') === filter)
  ).filter(matchesSearch);
  const pendingCount = vendors.filter((v) => v.approval_status === 'pending').length;
  const pendingLocationCount = vendors.filter((v) => v.location_approval_status === 'pending').length;

  const openOwnerLocation = (vendor) => {
    setOwnerLocDialog({
      open: true,
      vendor,
      saving: false,
      form: {
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        country: vendor.country || '',
        owner_name: vendor.owner_name || '',
        owner_email: vendor.owner_email || '',
        owner_phone: vendor.owner_phone || '',
        whatsapp_number: vendor.whatsapp_number || '',
        business_registration_id: vendor.business_registration_id || '',
        tax_id: vendor.tax_id || '',
        admin_notes: vendor.admin_notes || '',
      },
    });
  };
  const saveOwnerLocation = async () => {
    const { vendor, form } = ownerLocDialog;
    if (!vendor) return;
    setOwnerLocDialog((s) => ({ ...s, saving: true }));
    try {
      const payload = {};
      for (const [k, val] of Object.entries(form)) {
        payload[k] = (typeof val === 'string' ? val.trim() : val) || null;
      }
      await adminApi.updateVendor(vendor.id, payload);
      toast.success(`Owner & location saved for ${vendor.name}. This is now the source of truth across the app.`);
      setOwnerLocDialog({ open: false, vendor: null, form: null, saving: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      setOwnerLocDialog((s) => ({ ...s, saving: false }));
    }
  };

  const approveLocation = async (vendor) => {
    try {
      const doc = await adminLocationApprovalApi.approve(vendor.id);
      toast.success(`${vendor.name}: new store location approved — orders resumed.`);
      _offerWhatsAppLink(doc, `Ping ${vendor.name} on WhatsApp?`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const rejectLocation = async () => {
    const { vendor, reason } = locRejectDialog;
    if (!vendor) return;
    try {
      const doc = await adminLocationApprovalApi.reject(vendor.id, reason || null);
      toast.success(`${vendor.name}: location change rejected — previous location kept.`);
      _offerWhatsAppLink(doc, `Explain the rejection to ${vendor.name} on WhatsApp?`);
      setLocRejectDialog({ open: false, vendor: null, reason: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const _offerWhatsAppLink = (doc, label) => {
    const url = doc?._admin_wa_link;
    if (!url) return;
    toast(label, {
      duration: 8000,
      action: {
        label: 'Open WhatsApp',
        onClick: () => window.open(url, '_blank', 'noopener'),
      },
    });
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...empty, ...v, tags: (v.tags || []).join(', '), promo: v.promo || '' });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      tags: (form.tags || '').split(',').map((s) => s.trim()).filter(Boolean),
      promo: form.promo || null,
      rating: Number(form.rating),
      reviews: Number(form.reviews),
      deliveryFee: Number(form.deliveryFee),
      minOrder: Number(form.minOrder),
    };
    try {
      if (editing) {
        const { id, ...rest } = payload;
        await adminApi.updateVendor(editing.id, rest);
        toast.success('Vendor updated');
      } else {
        if (!payload.id) { toast.error('ID is required'); return; }
        await adminApi.createVendor(payload);
        toast.success('Vendor created');
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (v) => {
    try {
      await adminApi.updateVendor(v.id, { is_active: v.is_active === false ? true : false });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This also deletes its menu.`)) return;
    try { await adminApi.deleteVendor(v.id); toast.success('Vendor deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const unlinkOwner = async (v) => {
    if (!window.confirm(
      `Unlink owner from "${v.name}"?\n\nThis lets the previous owner re-onboard a different store (useful when a live merchant wants to migrate). The vendor itself stays — data + orders are preserved, but it becomes ownerless until you re-assign or delete it.`
    )) return;
    try {
      const { data } = await api.post(`/admin/vendors/${v.id}/unlink-owner`);
      toast.success(data?.message || 'Owner unlinked');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const reassignOwner = async () => {
    const { vendor, email } = reassignDialog;
    if (!email.trim()) { toast.error('Enter the new owner\'s email'); return; }
    setReassignDialog((s) => ({ ...s, busy: true }));
    try {
      // If the store already has an owner, transfer = unlink + reassign in one flow.
      // Frontend orchestrates it so we don't need a separate backend "transfer" endpoint.
      if (vendor.owner_user_id) {
        await api.post(`/admin/vendors/${vendor.id}/unlink-owner`);
      }
      const { data } = await api.post(`/admin/vendors/${vendor.id}/reassign-owner`, { email: email.trim() });
      toast.success(data?.message || 'Owner assigned');
      setReassignDialog({ open: false, vendor: null, email: '', busy: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      setReassignDialog((s) => ({ ...s, busy: false }));
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Vendors</h1>
          <p className="text-slate-500 mt-1">Manage stores, restaurants, and their menus.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-1.5" />New vendor</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? `Edit · ${editing.name}` : 'Create vendor'}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
              <FormField label="ID (unique)" value={form.id} onChange={(v) => setForm({ ...form, id: v })} disabled={!!editing} placeholder="v10" />
              <FormField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Bella Italia" />
              <FormField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="restaurants" />
              <FormField label="Cuisine" value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} placeholder="Italian" />
              <FormField label="Rating" type="number" value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
              <FormField label="Reviews" type="number" value={form.reviews} onChange={(v) => setForm({ ...form, reviews: v })} />
              <FormField label="Delivery time" value={form.deliveryTime} onChange={(v) => setForm({ ...form, deliveryTime: v })} />
              <FormField label="Delivery fee ($)" type="number" value={form.deliveryFee} onChange={(v) => setForm({ ...form, deliveryFee: v })} />
              <FormField label="Min order ($)" type="number" value={form.minOrder} onChange={(v) => setForm({ ...form, minOrder: v })} />
              <FormField label="Distance" value={form.distance} onChange={(v) => setForm({ ...form, distance: v })} />
              <FormField label="Tags (comma-sep)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="Pizza, Pasta" full />
              <FormField label="Cover image URL" value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} full />
              <FormField label="Logo URL (optional)" value={form.logo || ''} onChange={(v) => setForm({ ...form, logo: v })} full />
              <FormField label="Promo label (optional)" value={form.promo || ''} onChange={(v) => setForm({ ...form, promo: v })} placeholder="20% off" />
              <div className="flex items-center gap-2 mt-7">
                <input id="is_active" type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white">{editing ? 'Save changes' : 'Create vendor'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, owner email, address, city, tax ID, vendor #…"
          className="pl-9 pr-10 h-11 bg-white border-slate-200"
          data-testid="vendor-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center"
            aria-label="Clear search"
            data-testid="vendor-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {q && (
          <div className="mt-1.5 text-xs text-slate-500" data-testid="vendor-search-count">
            Showing <span className="font-semibold text-slate-800">{filtered.length}</span> of {vendors.length} vendors matching &ldquo;{search}&rdquo;
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5" /> Filter
        </span>
        {[
          { id: 'all',      label: 'All',      count: vendors.length },
          { id: 'pending',  label: 'Pending',  count: pendingCount, color: 'amber' },
          { id: 'approved', label: 'Approved', count: vendors.filter((v) => (v.approval_status || 'approved') === 'approved').length, color: 'emerald' },
          { id: 'rejected', label: 'Rejected', count: vendors.filter((v) => v.approval_status === 'rejected').length, color: 'rose' },
          { id: 'pending_location', label: 'Location changes', count: pendingLocationCount, color: 'amber' },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 h-8 rounded-full text-xs font-medium ${filter === f.id ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid={`vendor-filter-${f.id}`}>
            {f.label} <span className={`ml-1 ${filter === f.id ? 'text-emerald-300' : 'text-slate-400'}`}>{f.count}</span>
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((v) => {
          const status = v.approval_status || 'approved';
          return (
          <div key={v.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover" data-testid={`vendor-card-${v.id}`}>
            <div className="relative h-32 bg-slate-100">
              {v.cover && <img src={v.cover} alt={v.name} className="w-full h-full object-cover" />}
              {status === 'pending' && (
                <div className="absolute top-2 left-2">
                  <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-0">Pending approval</Badge>
                </div>
              )}
              {status === 'rejected' && (
                <div className="absolute top-2 left-2">
                  <Badge className="bg-rose-600 hover:bg-rose-600 text-white border-0">Rejected</Badge>
                </div>
              )}
              {v.location_approval_status === 'pending' && (
                <div className="absolute top-2 right-2">
                  <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-0 flex items-center gap-1" data-testid={`vendor-location-pending-badge-${v.id}`}>
                    <MapPin className="h-3 w-3" />Location pending
                  </Badge>
                </div>
              )}
              {v.is_active === false && status === 'approved' && (
                <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
                  <Badge className="bg-rose-600 hover:bg-rose-600 text-white border-0">Disabled</Badge>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{v.name}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">{v.category} · {v.cuisine} · <span className="font-mono">{v.currency || 'USD'}</span></div>
                </div>
                <div className="flex items-center gap-1 text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {v.rating}
                </div>
              </div>
              {status === 'rejected' && v.approval_reason && (
                <div className="mt-2 px-2 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-800 italic">
                  Reason: {v.approval_reason}
                </div>
              )}
              {(v.address || v.owner_name || v.owner_email) && (
                <div className="mt-2 px-2 py-1.5 rounded-md bg-slate-50 border border-slate-100 text-xs text-slate-600 space-y-0.5" data-testid={`vendor-owner-loc-summary-${v.id}`}>
                  {v.address && (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="h-3 w-3 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate" title={v.address}>{v.address}</span>
                    </div>
                  )}
                  {(v.owner_name || v.owner_email) && (
                    <div className="flex items-start gap-1.5">
                      <Users className="h-3 w-3 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">
                        {v.owner_name}{v.owner_name && v.owner_email ? ' · ' : ''}{v.owner_email}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {status === 'pending' && (
                <div className="mt-3 flex items-center gap-1.5">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" onClick={() => setApproval(v, 'approved')} data-testid={`approve-vendor-${v.id}`}>
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 flex-1"
                    onClick={() => setRejectDialog({ open: true, vendor: v, reason: '' })} data-testid={`reject-vendor-${v.id}`}>
                    <ShieldX className="h-3.5 w-3.5 mr-1" />Reject
                  </Button>
                </div>
              )}
              {v.location_approval_status === 'pending' && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                  <div className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-1">
                    <MapPin className="h-3 w-3" /> Location change awaiting approval
                  </div>
                  {v.pending_location?.address && (
                    <div className="text-xs text-amber-900 mb-2 truncate" title={v.pending_location.address} data-testid={`vendor-pending-address-${v.id}`}>
                      New: {v.pending_location.address}
                    </div>
                  )}
                  <div className="text-[10px] text-amber-700 mb-2">
                    Store is paused from taking new orders until approved.
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                      onClick={() => approveLocation(v)} data-testid={`approve-location-${v.id}`}>
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 flex-1"
                      onClick={() => setLocRejectDialog({ open: true, vendor: v, reason: '' })}
                      data-testid={`reject-location-${v.id}`}>
                      <ShieldX className="h-3.5 w-3.5 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => openEdit(v)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Link to={`/admin/vendors/${v.id}/menu`}><Button size="sm" variant="outline"><ListOrdered className="h-3.5 w-3.5 mr-1" />Menu</Button></Link>
                <Link to={`/vendor-admin?vendor_id=${v.id}`}><Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open as vendor</Button></Link>
                <Button size="sm" variant="outline" onClick={() => toggleActive(v)}><Power className="h-3.5 w-3.5 mr-1" />{v.is_active === false ? 'Enable' : 'Disable'}</Button>
                {v.owner_user_id ? (
                  <>
                    <Button size="sm" variant="outline"
                      onClick={() => setReassignDialog({ open: true, vendor: v, email: '', busy: false })}
                      className="text-emerald-700 hover:text-emerald-800 border-emerald-200 hover:bg-emerald-50"
                      data-testid={`transfer-owner-${v.id}`}
                      title="Transfer this store to a new owner (unlinks the current owner and assigns the new one in one step)">
                      <UserPlus className="h-3.5 w-3.5 mr-1" />Transfer owner
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => unlinkOwner(v)}
                      className="text-amber-700 hover:text-amber-800 border-amber-200 hover:bg-amber-50"
                      data-testid={`unlink-owner-${v.id}`}
                      title="Detach this vendor from its owner so they can onboard a different store">
                      <Unlink className="h-3.5 w-3.5 mr-1" />Unlink owner
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline"
                    onClick={() => setReassignDialog({ open: true, vendor: v, email: '', busy: false })}
                    className="text-emerald-700 hover:text-emerald-800 border-emerald-200 hover:bg-emerald-50"
                    data-testid={`reassign-owner-${v.id}`}
                    title="Assign this vendor to a user by email">
                    <UserPlus className="h-3.5 w-3.5 mr-1" />Assign owner
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => openOwnerLocation(v)}
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  data-testid={`owner-location-${v.id}`}
                  title="Set owner + store location. Only ShipitNow admins can change this.">
                  <MapPin className="h-3.5 w-3.5 mr-1" />Owner &amp; location
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOwnersDialog({ open: true, vendor: v })} className="text-indigo-700 hover:text-indigo-800 border-indigo-200 hover:bg-indigo-50" data-testid={`manage-owners-${v.id}`} title="Add / remove additional owner emails">
                  <Users className="h-3.5 w-3.5 mr-1" />Owners
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(v)} className="text-rose-600 hover:text-rose-700 border-rose-200"><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
              </div>
            </div>
          </div>
        );})}
      </div>

      <Dialog open={reassignDialog.open} onOpenChange={(o) => !o && setReassignDialog({ open: false, vendor: null, email: '', busy: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reassignDialog.vendor?.owner_user_id ? 'Transfer' : 'Assign'} owner {reassignDialog.vendor?.owner_user_id ? 'of' : 'to'} {reassignDialog.vendor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {reassignDialog.vendor?.owner_user_id && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2" data-testid="transfer-owner-warning">
                <div className="font-semibold mb-0.5">Currently owned by:</div>
                <div className="text-slate-700">{reassignDialog.vendor?.owner_name || reassignDialog.vendor?.owner_email || reassignDialog.vendor?.owner_user_id}</div>
                <div className="mt-1.5 text-[11px]">The current owner will lose vendor-admin access as soon as the new owner is assigned. All data, menu items, orders, and reviews stay with the store.</div>
              </div>
            )}
            <div className="text-sm text-slate-600">
              The new owner must already have a ShipitNow account and must <span className="font-medium">not</span> already own a different store.
              They&apos;ll get vendor-admin access immediately.
            </div>
            <Label>New owner email</Label>
            <Input
              type="email"
              value={reassignDialog.email}
              onChange={(e) => setReassignDialog((s) => ({ ...s, email: e.target.value }))}
              placeholder="owner@example.com"
              data-testid="reassign-email-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialog({ open: false, vendor: null, email: '', busy: false })}>Cancel</Button>
            <Button onClick={reassignOwner} disabled={reassignDialog.busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-reassign-btn">
              <UserPlus className="h-4 w-4 mr-1.5" />
              {reassignDialog.busy ? (reassignDialog.vendor?.owner_user_id ? 'Transferring…' : 'Assigning…') : (reassignDialog.vendor?.owner_user_id ? 'Transfer to new owner' : 'Assign owner')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog({ ...rejectDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject {rejectDialog.vendor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason (vendor will see this)</Label>
            <Textarea value={rejectDialog.reason} onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
              placeholder="e.g. Missing required food handler certification — re-submit when ready."
              rows={4} data-testid="reject-reason-textarea" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, vendor: null, reason: '' })}>Cancel</Button>
            <Button onClick={async () => {
              await setApproval(rejectDialog.vendor, 'rejected', rejectDialog.reason || null);
              setRejectDialog({ open: false, vendor: null, reason: '' });
            }} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-reject-btn">
              <ShieldX className="h-4 w-4 mr-1.5" />Reject vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ownerLocDialog.open} onOpenChange={(o) => !o && setOwnerLocDialog({ open: false, vendor: null, form: null, saving: false })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Owner &amp; location · {ownerLocDialog.vendor?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-3">
            Only ShipitNow admins can change these fields. This is the source of truth used throughout the app for driver dispatch, invoices, and customer-facing store info.
          </div>
          {ownerLocDialog.form && (
            <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-5" data-testid="owner-location-dialog">
              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" /> Store address
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Street address" value={ownerLocDialog.form.address} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, address: v } }))} placeholder="123 Bank St, Ottawa, ON" full />
                  <FormField label="City (Ottawa or Ibadan)" value={ownerLocDialog.form.city} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, city: v } }))} placeholder="Ottawa" />
                  <FormField label="State / Province" value={ownerLocDialog.form.state} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, state: v } }))} placeholder="Ontario" />
                  <FormField label="Country" value={ownerLocDialog.form.country} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, country: v } }))} placeholder="Canada" />
                </div>
              </section>

              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                  <Users className="h-3 w-3" /> Owner details
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Owner name" value={ownerLocDialog.form.owner_name} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, owner_name: v } }))} placeholder="Jane Doe" />
                  <FormField label="Owner email" value={ownerLocDialog.form.owner_email} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, owner_email: v } }))} placeholder="jane@shop.com" />
                  <FormField label="Owner phone" value={ownerLocDialog.form.owner_phone} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, owner_phone: v } }))} placeholder="+1 416 555 0100" />
                  <FormField label="WhatsApp number" value={ownerLocDialog.form.whatsapp_number} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, whatsapp_number: v } }))} placeholder="+1 416 555 0100" />
                </div>
              </section>

              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                  <Building2 className="h-3 w-3" /> Business identifiers
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Business registration #" value={ownerLocDialog.form.business_registration_id} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, business_registration_id: v } }))} placeholder="e.g. 1234567890 RC0001" />
                  <FormField label="Tax ID / GST #" value={ownerLocDialog.form.tax_id} onChange={(v) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, tax_id: v } }))} placeholder="e.g. 12345 6789 RT0001" />
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-600">Admin notes</Label>
                  <Textarea
                    value={ownerLocDialog.form.admin_notes}
                    onChange={(e) => setOwnerLocDialog((s) => ({ ...s, form: { ...s.form, admin_notes: e.target.value } }))}
                    placeholder="Internal notes visible only to ShipitNow admins…"
                    rows={3}
                    data-testid="owner-loc-notes-textarea"
                  />
                </div>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOwnerLocDialog({ open: false, vendor: null, form: null, saving: false })}>Cancel</Button>
            <Button
              onClick={saveOwnerLocation}
              disabled={ownerLocDialog.saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-owner-location-btn"
            >
              {ownerLocDialog.saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
              {ownerLocDialog.saving ? 'Saving…' : 'Save (admin override)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OwnersManagerDialog
        open={ownersDialog.open}
        vendor={ownersDialog.vendor}
        onClose={() => setOwnersDialog({ open: false, vendor: null })}
      />

      <Dialog open={locRejectDialog.open} onOpenChange={(o) => setLocRejectDialog({ ...locRejectDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject location change for {locRejectDialog.vendor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              The vendor will keep their previous location. Their store will be unfrozen and can take orders again.
            </div>
            <Label>Reason (vendor will see this)</Label>
            <Textarea
              value={locRejectDialog.reason}
              onChange={(e) => setLocRejectDialog({ ...locRejectDialog, reason: e.target.value })}
              placeholder="e.g. Address seems to be a residential apartment — please provide a commercial pickup address."
              rows={4}
              data-testid="location-reject-reason-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocRejectDialog({ open: false, vendor: null, reason: '' })}>Cancel</Button>
            <Button onClick={rejectLocation} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-reject-location-btn">
              <ShieldX className="h-4 w-4 mr-1.5" />Reject change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const OwnersManagerDialog = ({ open, vendor, onClose }) => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!vendor) return;
    setLoading(true);
    try {
      const { owners } = await adminOwnersApi.list(vendor.id);
      setOwners(owners || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open, vendor?.id]);// eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await adminOwnersApi.add(vendor.id, email.trim());
      toast.success('Owner added.');
      setEmail('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (u.is_primary) return;
    if (!window.confirm(`Remove ${u.email} as an owner of ${vendor?.name}?`)) return;
    try {
      await adminOwnersApi.remove(vendor.id, u.user_id);
      toast.success('Owner removed.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="owners-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" /> Owners — {vendor?.name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 -mt-1">
          Add multiple emails so several team members can manage this store. The <span className="font-semibold text-emerald-700">primary owner</span> cannot be removed here — use <span className="font-mono">Reassign</span> for that.
        </div>
        {loading ? (
          <div className="py-6 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {owners.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 p-2.5" data-testid={`owner-row-${u.user_id}`}>
                <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold shrink-0">
                  {(u.name || u.email || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {u.name || u.email}
                    {u.is_primary && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-emerald-700 font-bold">Primary</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                </div>
                {!u.is_primary && (
                  <button onClick={() => remove(u)} className="text-rose-600 hover:text-rose-700 p-1" aria-label="Remove owner" data-testid={`remove-owner-${u.user_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {owners.length === 0 && <div className="p-4 text-center text-sm text-slate-400">No owners yet.</div>}
          </div>
        )}
        <div className="pt-2 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">Add owner by email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" data-testid="add-owner-email-input" onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          </div>
          <Button onClick={add} disabled={busy || !email.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-owner-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, value, onChange, type = 'text', placeholder, disabled, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} className="mt-1 h-10" />
  </div>
);

export default VendorsPage;
